/**
 * Bundle Campaign Extension
 * 为product-bundle-builder添加活动页模式支持
 *
 * 活动页模式特点:
 * - Desktop: 左侧产品网格切换显示 + 右侧类目卡片
 * - 点击右侧类目卡片 → 左侧切换显示对应类目的产品网格
 * - Mobile: 保持原有逻辑(类目卡片 + Modal)
 *
 * 实现方式: 直接在已有的Web Component实例上添加活动页功能
 */

(function() {
  console.log('[Bundle Campaign Extension] Loading...');

  /**
   * 修复bundle-campaign的DOM位置
   * 确保它在#main-content内而不是直接在#wrapper下
   */
  function fixBundleCampaignPosition() {
    const bundleCampaign = document.querySelector('.bundle-builder--campaign');
    const mainContent = document.getElementById('main-content');

    if (!bundleCampaign || !mainContent) {
      console.log('[Bundle Campaign] Position fix skipped - elements not found');
      return;
    }

    // 检查是否已经在main-content内
    if (mainContent.contains(bundleCampaign)) {
      console.log('[Bundle Campaign] Already inside main-content');
      return;
    }

    // 获取包含bundle-campaign的shopify-section div
    const sectionWrapper = bundleCampaign.closest('.shopify-section');
    if (sectionWrapper) {
      console.log('[Bundle Campaign] Moving section to main-content');
      mainContent.appendChild(sectionWrapper);
      console.log('[Bundle Campaign] Position fixed - now inside main-content');
    }
  }

  /**
   * 初始化活动页模式
   */
  function initCampaignMode(builder) {
    console.log('[Bundle Campaign] Initializing campaign mode for builder:', builder);

    // 标记已初始化
    if (builder._campaignInitialized) {
      console.log('[Bundle Campaign] Already initialized');
      return;
    }
    builder._campaignInitialized = true;

    // 获取产品网格容器
    const productGridsContainer = builder.querySelector('[data-product-grids-container]');
    if (!productGridsContainer) {
      console.error('[Bundle Campaign] Product grids container not found');
      return;
    }

    builder._productGridsContainer = productGridsContainer;
    builder._activeCategory = null;

    // ✅ 关键修复: 确保state中有所有类目（活动页需要4个collection）
    ensureCampaignState(builder);

    // 设置产品网格内的产品事件监听（关键：Desktop模式下左侧产品网格的Add按钮）
    setupProductGridEvents(builder);

    // Desktop模式下,设置类目卡片点击行为
    if (window.innerWidth >= 990) {
      setupDesktopBehavior(builder);
    }

    // 监听窗口resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 990) {
          setupDesktopBehavior(builder);
        } else {
          restoreMobileBehavior(builder);
        }
      }, 250);
    });

    // ✅ 监听Modal打开，刷新Modal中的产品卡片状态
    setupModalOpenListener(builder);

    // ✅ 监听管理窗口删除事件，同步类目卡片display状态
    builder.addEventListener('bundle:manage-delete', (e) => {
      const { categoryId, variantId } = e.detail;
      console.log('[Bundle Campaign] Received manage delete event:', { categoryId, variantId });

      // 调用活动页专用的display修复
      fixCategoryCardDisplay(builder, categoryId);
      console.log('[Bundle Campaign] Fixed category card display after manage delete');
    });

    // ✅ 监听产品状态变化（Modal中的Add/Remove），同步类目卡片display状态
    builder.addEventListener('bundle:state-changed', (e) => {
      const { categoryId, variantId, action } = e.detail;
      console.log('[Bundle Campaign] Received state change event:', { categoryId, variantId, action });

      // 调用活动页专用的display修复
      fixCategoryCardDisplay(builder, categoryId);
      console.log('[Bundle Campaign] Fixed category card display after state change');
    });

    // 🔧 初始化View More/Less功能
    setupViewMoreLess(builder);

    // 默认激活第一个类目
    const firstGrid = productGridsContainer.querySelector('[data-product-grid]');
    if (firstGrid) {
      builder._activeCategory = firstGrid.dataset.gridCategory;
      updateCategoryCardState(builder, builder._activeCategory);
      console.log('[Bundle Campaign] Default category:', builder._activeCategory);
    }
  }

  /**
   * 确保state中有所有活动页需要的collection
   * 原始builder只初始化了collection_1/2/3，活动页需要collection_4
   */
  function ensureCampaignState(builder) {
    if (!builder.state) {
      console.error('[Bundle Campaign] Builder state not found!');
      return;
    }

    if (!builder.state.collections) {
      builder.state.collections = {};
    }

    // 查找页面中所有的类目卡片，确保state中有对应的collection
    const categoryCards = builder.querySelectorAll('.bundle-category-card');
    categoryCards.forEach(card => {
      const categoryId = card.dataset.categoryId;
      if (!builder.state.collections[categoryId]) {
        console.log('[Bundle Campaign] Adding missing collection to state:', categoryId);
        builder.state.collections[categoryId] = {
          selectedProducts: new Map(),
          currentPage: 1
        };
      }
    });

    console.log('[Bundle Campaign] State collections:', Object.keys(builder.state.collections));
  }

  /**
   * 设置产品网格内的产品事件监听
   * 处理Desktop模式下左侧产品网格中的Add/Remove按钮
   */
  function setupProductGridEvents(builder) {
    console.log('[Bundle Campaign] Setting up product grid events');

    // 监听产品网格容器内的点击事件
    builder._productGridsContainer.addEventListener('click', (e) => {
      // 🔧 修复：排除Modal内的点击，避免重复处理
      // Modal内的点击由product-bundle-builder.js处理
      if (e.target.closest('.bundle-modal')) {
        console.log('[Bundle Campaign] Click in modal - skipping (handled by main builder)');
        return;
      }

      // Add+ 按钮
      if (e.target.closest('[data-add-to-bundle]')) {
        e.preventDefault();
        const productCard = e.target.closest('.bundle-product-card');
        console.log('[Bundle Campaign] Add button clicked in grid', productCard);

        // 调用活动页专用的Add方法
        handleCampaignProductAdd(builder, productCard);
      }

      // 删除按钮
      if (e.target.closest('[data-remove-product]')) {
        e.preventDefault();
        const productCard = e.target.closest('.bundle-product-card');
        console.log('[Bundle Campaign] Remove button clicked in grid', productCard);

        // 调用活动页专用的Remove方法
        handleCampaignProductRemove(builder, productCard);
      }

      // 变体选择器 - 改为监听change事件
    });

    // 监听变体选择器的change事件
    builder._productGridsContainer.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-selector]')) {
        const variantSelector = e.target;
        const productCard = variantSelector.closest('.bundle-product-card');
        console.log('[Bundle Campaign] Variant changed in grid', productCard);

        // 调用活动页专用的变体切换方法
        handleCampaignVariantChange(builder, variantSelector, productCard);
      }
    });

    // 🔧 监听类目卡片的删除按钮（单产品状态）
    builder.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-all]')) {
        const categoryCard = e.target.closest('.bundle-category-card');
        if (!categoryCard) return;

        const categoryId = categoryCard.dataset.categoryId;
        console.log('[Bundle Campaign] Remove all button clicked for category:', categoryId);

        // 阻止默认行为和事件传播（避免product-bundle-builder.js重复处理）
        e.preventDefault();
        e.stopImmediatePropagation();

        // ✅ 先获取所有要删除的产品variantId
        const variantIdsToRemove = [];
        if (builder.state.collections[categoryId]) {
          const selectedProducts = builder.state.collections[categoryId].selectedProducts;
          selectedProducts.forEach((product, variantId) => {
            variantIdsToRemove.push(variantId);
          });
        }

        console.log('[Bundle Campaign] Variants to remove:', variantIdsToRemove);

        // 调用原始的removeAllProducts方法
        if (builder.removeAllProducts) {
          builder.removeAllProducts(categoryId);

          // ✅ 同步所有产品卡片状态（产品网格 + Modal）
          variantIdsToRemove.forEach(variantId => {
            updateAllProductCardsState(builder, variantId, categoryId);
          });

          // 手动修复display状态（活动页需要）
          fixCategoryCardDisplay(builder, categoryId);

          console.log('[Bundle Campaign] Remove all complete, all product cards synced');
        }
      }
    }, true); // 使用capture阶段，优先于product-bundle-builder.js的监听器
  }

  /**
   * 设置Desktop行为: 点击类目卡片→左侧切换产品网格
   */
  function setupDesktopBehavior(builder) {
    console.log('[Bundle Campaign] Setting up desktop behavior');

    // 移除之前的监听器
    if (builder._campaignClickHandler) {
      builder.removeEventListener('click', builder._campaignClickHandler, true);
    }

    // 创建点击处理器
    builder._campaignClickHandler = function(e) {
      // 只处理类目卡片点击
      const categoryCard = e.target.closest('.bundle-category-card');
      if (!categoryCard) return;

      // 如果点击了管理按钮或删除按钮,不处理
      if (e.target.closest('[data-manage-products]') || e.target.closest('[data-remove-all]')) {
        return;
      }

      // Desktop模式: 阻止Modal打开
      if (window.innerWidth >= 990) {
        const modalOpener = e.target.closest('modal-opener');
        if (modalOpener) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const categoryId = categoryCard.dataset.categoryId;
          console.log('[Bundle Campaign] Category card clicked:', categoryId);

          // 切换显示对应的产品网格
          switchProductGrid(builder, categoryId);
          return false;
        }
      }
    };

    // 使用capture阶段,优先于modal-opener
    builder.addEventListener('click', builder._campaignClickHandler, true);
  }

  /**
   * 恢复Mobile的Modal行为
   */
  function restoreMobileBehavior(builder) {
    console.log('[Bundle Campaign] Restoring mobile behavior');

    if (builder._campaignClickHandler) {
      builder.removeEventListener('click', builder._campaignClickHandler, true);
      builder._campaignClickHandler = null;
    }
  }

  /**
   * 监听Modal打开事件，刷新Modal中的产品卡片状态
   */
  function setupModalOpenListener(builder) {
    console.log('[Bundle Campaign] Setting up modal open listener');

    // 查找所有Bundle Modal
    const modals = document.querySelectorAll('.bundle-modal[data-category-id]');

    modals.forEach(modal => {
      const categoryId = modal.dataset.categoryId;
      console.log('[Bundle Campaign] Found modal for category:', categoryId);

      // 使用MutationObserver监听Modal的open属性变化
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (modal.hasAttribute('open')) {
              console.log('[Bundle Campaign] Modal opened for category:', categoryId);
              // Modal打开时，刷新Modal中所有产品卡片的状态
              refreshModalProductCards(builder, categoryId);
            }
            // Modal关闭时不需要特别处理
          }
        });
      });

      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open']
      });

      // 保存observer引用以便后续清理
      if (!builder._modalObservers) {
        builder._modalObservers = [];
      }
      builder._modalObservers.push(observer);
    });
  }

  /**
   * 刷新Modal中所有产品卡片的选中状态
   */
  function refreshModalProductCards(builder, categoryId) {
    console.log('[Bundle Campaign] Refreshing modal product cards for:', categoryId);

    const modal = document.querySelector(`#BundleModal-${categoryId}`);
    if (!modal) {
      console.error('[Bundle Campaign] Modal not found:', categoryId);
      return;
    }

    const selectedProducts = builder.state.collections[categoryId]?.selectedProducts;
    if (!selectedProducts) {
      console.error('[Bundle Campaign] No selected products for category:', categoryId);
      return;
    }

    // 遍历Modal中的所有产品卡片，更新状态
    const productCards = modal.querySelectorAll('.bundle-product-card[data-category-id]');
    console.log('[Bundle Campaign] Found product cards in modal:', productCards.length);

    productCards.forEach(card => {
      const variantId = parseInt(card.dataset.variantId);
      if (builder.updateProductCardState) {
        builder.updateProductCardState(card, categoryId);
      }
    });

    console.log('[Bundle Campaign] Modal product cards refreshed');
  }

  /**
   * 切换显示指定类目的产品网格
   */
  function switchProductGrid(builder, categoryId) {
    console.log('[Bundle Campaign] Switching to category:', categoryId);

    builder._activeCategory = categoryId;

    // 隐藏所有产品网格,只显示选中的
    const allGrids = builder._productGridsContainer.querySelectorAll('[data-product-grid]');
    allGrids.forEach(grid => {
      if (grid.dataset.gridCategory === categoryId) {
        grid.style.display = 'block';
        console.log('[Bundle Campaign] Showing grid:', categoryId);
      } else {
        grid.style.display = 'none';
      }
    });

    // 更新类目卡片激活状态
    updateCategoryCardState(builder, categoryId);

    // ✅ 更新统一面板中空槽位的激活状态
    updateUnifiedPanelActiveState(builder, categoryId);
  }

  /**
   * 更新统一面板中空槽位的激活状态
   */
  function updateUnifiedPanelActiveState(builder, categoryId) {
    const panel = builder.querySelector('[data-unified-panel]');
    if (!panel) return;

    const allEmptySlots = panel.querySelectorAll('[data-unified-empty]');
    allEmptySlots.forEach(slot => {
      if (slot.dataset.categoryId === categoryId) {
        slot.classList.add('active');
      } else {
        slot.classList.remove('active');
      }
    });
  }

  /**
   * 更新类目卡片激活状态
   */
  function updateCategoryCardState(builder, categoryId) {
    builder.querySelectorAll('.bundle-category-card').forEach(card => {
      if (card.dataset.categoryId === categoryId) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });
  }

  /**
   * 活动页专用：处理产品添加
   * 复制原始handleProductAdd的逻辑，但从产品卡片的data-category-id获取类目
   */
  function handleCampaignProductAdd(builder, productCard) {
    console.log('[Bundle Campaign] handleCampaignProductAdd called');
    console.log('[Bundle Campaign] Product card:', productCard);
    console.log('[Bundle Campaign] Product card dataset:', productCard.dataset);

    // 如果有变体选择器，使用选择器的值
    const variantSelector = productCard.querySelector('[data-variant-selector]');
    let variantId;

    if (variantSelector) {
      const selectedOption = variantSelector.selectedOptions[0];
      variantId = parseInt(selectedOption.value);
      console.log('[Bundle Campaign] Got variant ID from selector:', variantId);

      // 同步更新产品卡片的 data-variant-id
      productCard.dataset.variantId = variantId;
      productCard.dataset.price = selectedOption.dataset.price;
      productCard.dataset.comparePrice = selectedOption.dataset.comparePrice || '';
    } else {
      variantId = parseInt(productCard.dataset.variantId);
      console.log('[Bundle Campaign] Got variant ID from data attribute:', variantId);
    }

    // 验证 variantId
    if (!variantId || isNaN(variantId)) {
      console.error('[Bundle Campaign] Invalid variant ID:', variantId);
      alert('Error: Cannot find variant. Please refresh and try again.');
      return;
    }

    // 从产品卡片的data-category-id获取categoryId
    const categoryId = productCard.dataset.categoryId;
    const available = productCard.dataset.available === 'true';

    console.log('[Bundle Campaign] Variant ID:', variantId);
    console.log('[Bundle Campaign] Category ID:', categoryId);
    console.log('[Bundle Campaign] Available:', available);

    if (!categoryId) {
      console.error('[Bundle Campaign] Category ID not found on product card');
      return;
    }

    // 检查库存
    if (!available) {
      console.warn('[Bundle Campaign] Product is out of stock');
      alert('This product is currently out of stock');
      return;
    }

    // 检查state是否存在
    if (!builder.state || !builder.state.collections || !builder.state.collections[categoryId]) {
      console.error('[Bundle Campaign] State not found for category:', categoryId);
      console.error('[Bundle Campaign] Builder state:', builder.state);
      return;
    }

    const selectedProducts = builder.state.collections[categoryId].selectedProducts;
    console.log('[Bundle Campaign] Current selected products:', selectedProducts);

    if (selectedProducts.has(variantId)) {
      // 数量+1
      const product = selectedProducts.get(variantId);
      product.quantity += 1;
      console.log('[Bundle Campaign] Increased quantity to:', product.quantity);
    } else {
      // 新增产品
      const newProduct = {
        productId: parseInt(productCard.dataset.productId),
        productHandle: productCard.dataset.productHandle, // 🔧 存储产品handle
        variantId: variantId,
        quantity: 1,
        price: parseFloat(productCard.dataset.price) / 100,
        compareAtPrice: parseFloat(productCard.dataset.comparePrice || 0) / 100,
        title: productCard.dataset.title,
        image: productCard.dataset.image,
        available: available
      };

      // 🔧 提取产品的所有变体信息（如果有变体选择器）
      const variantSelector = productCard.querySelector('[data-variant-selector]');
      if (variantSelector) {
        const variants = [];
        Array.from(variantSelector.options).forEach(option => {
          variants.push({
            id: parseInt(option.value),
            title: option.text,
            price: parseFloat(option.dataset.price) / 100,
            compareAtPrice: parseFloat(option.dataset.comparePrice || 0) / 100,
            image: option.dataset.image || newProduct.image
          });
        });
        newProduct.variants = variants;
        console.log('[Bundle Campaign] Extracted variants:', variants);
      }

      // 验证产品数据
      if (!newProduct.productId || !newProduct.variantId || !newProduct.price) {
        console.error('[Bundle Campaign] Invalid product data:', newProduct);
        alert('Error: Invalid product data. Please refresh and try again.');
        return;
      }

      selectedProducts.set(variantId, newProduct);
      console.log('[Bundle Campaign] Added new product:', newProduct);
    }

    // 更新UI - 调用原始builder的方法
    console.log('[Bundle Campaign] Calling UI update methods...');
    console.log('[Bundle Campaign] builder.updateProductCardState exists?', typeof builder.updateProductCardState);
    console.log('[Bundle Campaign] builder.updateCategoryCard exists?', typeof builder.updateCategoryCard);

    // ✅ 同步更新产品网格和Modal中的产品卡片状态
    try {
      updateAllProductCardsState(builder, variantId, categoryId);
      console.log('[Bundle Campaign] All product cards state updated successfully');
    } catch (error) {
      console.error('[Bundle Campaign] Error updating product cards state:', error);
    }

    if (builder.updateCategoryCard) {
      try {
        console.log('[Bundle Campaign] Calling updateCategoryCard for:', categoryId);
        console.log('[Bundle Campaign] State before update:', builder.state.collections[categoryId]);

        // 调用原始方法（更新state、渲染内容）
        builder.updateCategoryCard(categoryId);

        console.log('[Bundle Campaign] updateCategoryCard called successfully');

        // ✅ 手动修复display切换（因为原始方法的display设置不生效）
        fixCategoryCardDisplay(builder, categoryId);

      } catch (error) {
        console.error('[Bundle Campaign] Error calling updateCategoryCard:', error);
      }
    } else {
      console.error('[Bundle Campaign] updateCategoryCard method not found');
    }

    if (builder.updateCartBadges) {
      try {
        builder.updateCartBadges();
        console.log('[Bundle Campaign] updateCartBadges called successfully');
      } catch (error) {
        console.error('[Bundle Campaign] Error calling updateCartBadges:', error);
      }
    }

    if (builder.updateAddToCartButton) {
      try {
        builder.updateAddToCartButton();
        console.log('[Bundle Campaign] updateAddToCartButton called successfully');
      } catch (error) {
        console.error('[Bundle Campaign] Error calling updateAddToCartButton:', error);
      }
    }

    console.log('[Bundle Campaign] Product added, UI update complete');
  }

  /**
   * 活动页专用：处理产品移除
   */
  function handleCampaignProductRemove(builder, productCard) {
    console.log('[Bundle Campaign] handleCampaignProductRemove called');

    const variantId = parseInt(productCard.dataset.variantId);
    const categoryId = productCard.dataset.categoryId;

    console.log('[Bundle Campaign] Removing variant:', variantId, 'from category:', categoryId);

    if (!categoryId || !builder.state || !builder.state.collections || !builder.state.collections[categoryId]) {
      console.error('[Bundle Campaign] Invalid state for removal');
      return;
    }

    // 直接清零（移除）
    builder.state.collections[categoryId].selectedProducts.delete(variantId);

    // 更新UI - 同步更新产品网格和Modal中的卡片状态
    try {
      updateAllProductCardsState(builder, variantId, categoryId);
      console.log('[Bundle Campaign] All product cards state updated after removal');
    } catch (error) {
      console.error('[Bundle Campaign] Error updating product cards state:', error);
    }

    if (builder.updateCategoryCard) {
      builder.updateCategoryCard(categoryId);

      // ✅ 手动修复display切换
      fixCategoryCardDisplay(builder, categoryId);
    }

    if (builder.updateCartBadges) {
      builder.updateCartBadges();
    }

    if (builder.updateAddToCartButton) {
      builder.updateAddToCartButton();
    }

    console.log('[Bundle Campaign] Product removed, UI updated');
  }

  /**
   * 同步更新产品网格和Modal中的产品卡片状态
   * 确保Desktop和Mobile的卡片状态一致
   */
  function updateAllProductCardsState(builder, variantId, categoryId) {
    console.log('[Bundle Campaign] Updating all product cards for variant:', variantId, 'category:', categoryId);

    // 1️⃣ 更新产品网格中的卡片（Desktop）
    if (builder._productGridsContainer) {
      const gridCard = builder._productGridsContainer.querySelector(
        `.bundle-product-card[data-variant-id="${variantId}"][data-category-id="${categoryId}"]`
      );
      if (gridCard && builder.updateProductCardState) {
        console.log('[Bundle Campaign] Updating grid card');
        builder.updateProductCardState(gridCard, categoryId);
      }
    }

    // 2️⃣ 更新Modal中的卡片（Mobile）
    const modal = document.querySelector(`#BundleModal-${categoryId}`);
    if (modal) {
      const modalCard = modal.querySelector(
        `.bundle-product-card[data-variant-id="${variantId}"][data-category-id="${categoryId}"]`
      );
      if (modalCard && builder.updateProductCardState) {
        console.log('[Bundle Campaign] Updating modal card');
        builder.updateProductCardState(modalCard, categoryId);
      }
    }

    console.log('[Bundle Campaign] All product cards updated');
  }

  /**
   * 活动页专用：处理变体切换
   * 从产品卡片的data-category-id获取类目（而非从Modal）
   */
  function handleCampaignVariantChange(builder, select, productCard) {
    console.log('[Bundle Campaign] handleCampaignVariantChange called');

    const selectedOption = select.selectedOptions[0];
    const newVariantId = parseInt(selectedOption.value);
    const oldVariantId = parseInt(productCard.dataset.variantId);
    const categoryId = productCard.dataset.categoryId; // ✅ 从产品卡片获取

    if (!categoryId) {
      console.error('[Bundle Campaign] Category ID not found on product card');
      return;
    }

    console.log('[Bundle Campaign] Variant change:', {
      oldVariantId,
      newVariantId,
      categoryId
    });

    // 1️⃣ 更新产品卡片的data属性
    productCard.dataset.variantId = newVariantId;
    productCard.dataset.price = selectedOption.dataset.price;
    productCard.dataset.comparePrice = selectedOption.dataset.comparePrice || '';
    productCard.dataset.image = selectedOption.dataset.image || productCard.dataset.image;

    // 2️⃣ 更新产品图片
    const img = productCard.querySelector('[data-product-card-image]');
    if (img && selectedOption.dataset.image) {
      img.src = selectedOption.dataset.image;
      console.log('[Bundle Campaign] Updated image:', selectedOption.dataset.image);
    }

    // 3️⃣ 更新产品链接（跳转到新变体详情页）
    const productLink = productCard.querySelector('[data-product-link]');
    if (productLink) {
      const productHandle = productCard.dataset.productHandle;
      productLink.href = `/products/${productHandle}?variant=${newVariantId}`;
    }

    // 4️⃣ 更新价格显示
    const priceDisplay = productCard.querySelector('[data-product-card-price]');
    if (priceDisplay) {
      const comparePrice = parseFloat(selectedOption.dataset.comparePrice || 0) / 100;
      const currentPrice = parseFloat(selectedOption.dataset.price) / 100;

      // 使用全局 formatMoney 或回退到 theme 设置
      const fallbackFormatMoney = (amount) => {
        if (typeof window.formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
          return window.formatMoney(amount * 100, window.theme.settings.money_with_currency_format);
        }
        return amount.toFixed(2);
      };

      const comparePriceEl = priceDisplay.querySelector('[data-compare-price]');
      const currentPriceEl = priceDisplay.querySelector('[data-current-price]');

      if (comparePriceEl && comparePrice > currentPrice) {
        comparePriceEl.innerHTML = builder.formatMoney
          ? builder.formatMoney(comparePrice)
          : fallbackFormatMoney(comparePrice);
        comparePriceEl.style.display = '';
      } else if (comparePriceEl) {
        comparePriceEl.style.display = 'none';
      }

      if (currentPriceEl) {
        currentPriceEl.innerHTML = builder.formatMoney
          ? builder.formatMoney(currentPrice)
          : fallbackFormatMoney(currentPrice);
      }

      console.log('[Bundle Campaign] Updated price:', {
        currentPrice,
        comparePrice
      });
    }

    // 5️⃣ 更新产品卡片的选中状态UI
    // 如果旧变体已选中，切换到新变体后，新变体可能未选中，需要更新UI
    if (builder.updateProductCardState) {
      builder.updateProductCardState(productCard, categoryId);
    }

    console.log('[Bundle Campaign] Variant change complete');
  }

  /**
   * 手动修复类目卡片的display切换
   * 因为原始updateCategoryCard的display设置在活动页不生效
   */
  function fixCategoryCardDisplay(builder, categoryId) {
    const card = builder.querySelector(`.bundle-category-card[data-category-id="${categoryId}"]`);
    if (!card) {
      console.error('[Bundle Campaign] Category card not found for display fix:', categoryId);
      return;
    }

    const selectedProducts = builder.state.collections[categoryId].selectedProducts;
    const variantCount = selectedProducts.size;
    let totalQuantity = 0;
    selectedProducts.forEach(product => {
      totalQuantity += product.quantity;
    });

    const emptyState = card.querySelector('[data-category-empty]');
    const singleState = card.querySelector('[data-category-single]');
    const multipleState = card.querySelector('[data-category-multiple]');

    console.log('[Bundle Campaign] Fixing display:', {
      categoryId,
      variantCount,
      totalQuantity
    });

    if (variantCount === 0) {
      // 空状态
      card.dataset.state = 'empty';
      if (emptyState) emptyState.style.display = 'flex';
      if (singleState) singleState.style.display = 'none';
      if (multipleState) multipleState.style.display = 'none';
      console.log('[Bundle Campaign] Set to EMPTY state');

    } else if (variantCount === 1 && totalQuantity === 1) {
      // 单产品状态
      card.dataset.state = 'selected-single';
      if (emptyState) emptyState.style.display = 'none';
      if (singleState) singleState.style.display = 'flex';
      if (multipleState) multipleState.style.display = 'none';

      // ✅ 手动渲染单产品内容
      renderSingleProductContent(builder, card, categoryId);
      console.log('[Bundle Campaign] Set to SINGLE state');

    } else {
      // 多产品状态
      card.dataset.state = 'selected-multiple';
      if (emptyState) emptyState.style.display = 'none';
      if (singleState) singleState.style.display = 'none';
      if (multipleState) multipleState.style.display = 'flex';

      // ✅ 手动渲染多产品内容
      renderMultipleProductsContent(builder, card, categoryId);
      console.log('[Bundle Campaign] Set to MULTIPLE state');
    }
  }

  /**
   * 渲染单产品内容（复制自原始renderSingleProduct逻辑）
   */
  function renderSingleProductContent(builder, card, categoryId) {
    const selectedProducts = builder.state.collections[categoryId].selectedProducts;
    const [variantId, product] = Array.from(selectedProducts.entries())[0];

    const singleState = card.querySelector('[data-category-single]');
    if (!singleState) return;

    // 更新图片
    const imageContainer = singleState.querySelector('[data-product-image]');
    if (imageContainer) {
      imageContainer.innerHTML = `<img src="${product.image}" alt="${product.title}" loading="lazy">`;
    }

    // 更新标题
    const titleEl = singleState.querySelector('[data-product-title]');
    if (titleEl) {
      titleEl.textContent = product.title;
    }

    // 更新价格
    const priceDisplay = singleState.querySelector('[data-product-price]');
    if (priceDisplay) {
      // 使用全局 formatMoney 或回退到 theme 设置
      const fallbackFormatMoney = (amount) => {
        if (typeof window.formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
          return window.formatMoney(amount * 100, window.theme.settings.money_with_currency_format);
        }
        return amount.toFixed(2);
      };

      const comparePriceEl = priceDisplay.querySelector('[data-compare-price]');
      const currentPriceEl = priceDisplay.querySelector('[data-current-price]');

      if (comparePriceEl && product.compareAtPrice > product.price) {
        comparePriceEl.innerHTML = builder.formatMoney ? builder.formatMoney(product.compareAtPrice) : fallbackFormatMoney(product.compareAtPrice);
        comparePriceEl.style.display = '';
      } else if (comparePriceEl) {
        comparePriceEl.style.display = 'none';
      }

      if (currentPriceEl) {
        currentPriceEl.innerHTML = builder.formatMoney ? builder.formatMoney(product.price) : fallbackFormatMoney(product.price);
      }
    }

    console.log('[Bundle Campaign] Rendered single product:', product.title);
  }

  /**
   * 渲染多产品内容（复制自原始renderMultipleProducts逻辑）
   */
  function renderMultipleProductsContent(builder, card, categoryId) {
    const selectedProducts = Array.from(builder.state.collections[categoryId].selectedProducts.values());
    const variantCount = selectedProducts.length;
    const totalQuantity = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

    const multipleState = card.querySelector('[data-category-multiple]');
    if (!multipleState) return;

    // 渲染扑克牌
    const stackContainer = multipleState.querySelector('[data-product-stack]');
    if (stackContainer) {
      const maxDisplay = 3;
      const productsToShow = selectedProducts.slice(0, maxDisplay);

      let stackHTML = productsToShow.map(product =>
        `<img src="${product.image}" alt="${product.title}" loading="lazy">`
      ).join('');

      if (variantCount > maxDisplay) {
        stackHTML += '<span class="ellipsis">...</span>';
      }

      stackContainer.innerHTML = stackHTML;
    }

    // 更新数量显示
    const countEl = multipleState.querySelector('[data-stack-count]');
    if (countEl) {
      countEl.textContent = totalQuantity;
    }

    // 更新总价
    const totalPrice = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const totalComparePrice = selectedProducts.reduce((sum, p) => {
      return sum + (p.compareAtPrice > p.price ? p.compareAtPrice * p.quantity : 0);
    }, 0);

    // 使用全局 formatMoney 或回退到 theme 设置
    const fallbackFormatMoney = (amount) => {
      if (typeof window.formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
        return window.formatMoney(amount * 100, window.theme.settings.money_with_currency_format);
      }
      return amount.toFixed(2);
    };

    const stackPriceEl = multipleState.querySelector('[data-stack-price]');
    if (stackPriceEl) {
      const currentPriceEl = stackPriceEl.querySelector('[data-stack-current-price]');
      const comparePriceEl = stackPriceEl.querySelector('[data-stack-compare-price]');

      if (currentPriceEl) {
        currentPriceEl.innerHTML = builder.formatMoney ? builder.formatMoney(totalPrice) : fallbackFormatMoney(totalPrice);
      }

      if (comparePriceEl && totalComparePrice > 0) {
        comparePriceEl.innerHTML = builder.formatMoney ? builder.formatMoney(totalComparePrice) : fallbackFormatMoney(totalComparePrice);
        comparePriceEl.style.display = '';
      } else if (comparePriceEl) {
        comparePriceEl.style.display = 'none';
      }
    }

    console.log('[Bundle Campaign] Rendered multiple products:', totalQuantity, 'items');
  }

  /**
   * ============================================
   * 移动端统一Bundle面板渲染逻辑
   * ============================================
   */

  /**
   * 获取类目信息（标题和图标）
   */
  function getCategoryInfo(builder, categoryId) {
    const categoryCard = builder.querySelector(`.bundle-category-card[data-category-id="${categoryId}"]`);
    if (!categoryCard) return null;

    const titleEl = categoryCard.querySelector('.bundle-category-title');
    const iconEl = categoryCard.querySelector('.bundle-category-icon img, .bundle-category-icon .bundle-icon-svg');

    return {
      categoryId,
      title: titleEl ? titleEl.textContent : categoryId,
      icon: iconEl ? (iconEl.tagName === 'IMG' ? iconEl.src : iconEl.innerHTML) : '',
      iconType: iconEl && iconEl.tagName === 'IMG' ? 'image' : 'svg'
    };
  }

  /**
   * 获取未选产品的类目列表
   */
  function getEmptyCategories(builder) {
    const emptyCategories = [];
    const allCategories = ['collection_1', 'collection_2', 'collection_3', 'collection_4'];

    allCategories.forEach(categoryId => {
      if (builder.state.collections[categoryId]) {
        const selectedProducts = builder.state.collections[categoryId].selectedProducts;
        if (selectedProducts.size === 0) {
          const info = getCategoryInfo(builder, categoryId);
          if (info) {
            emptyCategories.push(info);
          }
        }
      }
    });

    return emptyCategories;
  }

  /**
   * 创建产品列表项HTML
   */
  function createProductListItem(product, categoryInfo, builder) {
    // 使用builder的formatMoney方法格式化价格，或使用全局formatMoney
    const formatMoney = builder && builder.formatMoney
      ? (amount) => builder.formatMoney(amount)
      : (amount) => {
          if (typeof window.formatMoney === 'function' && window.theme?.settings?.money_with_currency_format) {
            return `<span class="transcy-money">${window.formatMoney(amount * 100, window.theme.settings.money_with_currency_format)}</span>`;
          }
          return `<span class="transcy-money">${amount.toFixed(2)}</span>`;
        };

    const currentPriceHTML = formatMoney(product.price);
    const comparePriceHTML = product.compareAtPrice > product.price
      ? `<s class="unified-item-compare-price">${formatMoney(product.compareAtPrice)}</s>`
      : '';

    return `
      <div class="bundle-unified-item" data-unified-item data-variant-id="${product.variantId}" data-category-id="${categoryInfo.categoryId}">
        <div class="bundle-unified-item-image">
          <img src="${product.image}" alt="${product.title}" loading="lazy">
        </div>
        <div class="bundle-unified-item-info">
          <h4 class="bundle-unified-item-title">${product.title}</h4>
          <div class="bundle-unified-item-price-wrapper">
            <span class="unified-item-price">${currentPriceHTML}</span>
            ${comparePriceHTML}
          </div>
        </div>
        <button
          type="button"
          class="bundle-unified-item-remove"
          data-unified-remove
          aria-label="Remove ${product.title}">
          ×
        </button>
      </div>
    `;
  }

  /**
   * 创建空状态槽位HTML
   */
  function createEmptySlot(categoryInfo) {
    const iconHTML = categoryInfo.iconType === 'image'
      ? `<img src="${categoryInfo.icon}" alt="${categoryInfo.title}">`
      : categoryInfo.icon;

    return `
      <div class="bundle-unified-empty" data-unified-empty data-category-id="${categoryInfo.categoryId}">
        <div class="bundle-unified-empty-icon">
          ${iconHTML}
        </div>
        <div class="bundle-unified-empty-info">
          <h4 class="bundle-unified-empty-title">${categoryInfo.title}</h4>
          <span class="bundle-unified-empty-hint">Add Product</span>
        </div>
        <button
          type="button"
          class="bundle-unified-empty-add"
          data-unified-add-empty
          aria-label="Add ${categoryInfo.title}">
          +
        </button>
      </div>
    `;
  }

  /**
   * 渲染统一Bundle面板
   */
  function renderUnifiedBundlePanel(builder) {
    console.log('[Bundle Campaign] Rendering unified bundle panel');

    const panel = builder.querySelector('[data-unified-panel]');
    if (!panel) {
      console.log('[Bundle Campaign] Unified panel not found, skipping render');
      return;
    }

    const listContainer = panel.querySelector('[data-unified-product-list]');
    if (!listContainer) {
      console.error('[Bundle Campaign] Unified product list container not found');
      return;
    }

    // 收集所有已选产品
    const allProducts = [];
    const allCategories = ['collection_1', 'collection_2', 'collection_3', 'collection_4'];

    allCategories.forEach(categoryId => {
      if (builder.state.collections[categoryId]) {
        const selectedProducts = builder.state.collections[categoryId].selectedProducts;
        const categoryInfo = getCategoryInfo(builder, categoryId);

        if (categoryInfo) {
          selectedProducts.forEach((product, variantId) => {
            allProducts.push({
              ...product,
              categoryId: categoryId,
              categoryTitle: categoryInfo.title
            });
          });
        }
      }
    });

    console.log('[Bundle Campaign] Total products:', allProducts.length);

    // 清空列表
    listContainer.innerHTML = '';

    // 渲染已选产品
    allProducts.forEach(product => {
      const categoryInfo = getCategoryInfo(builder, product.categoryId);
      if (categoryInfo) {
        const itemHTML = createProductListItem(product, categoryInfo, builder);
        listContainer.insertAdjacentHTML('beforeend', itemHTML);
      }
    });

    // 渲染空状态槽位
    const emptyCategories = getEmptyCategories(builder);
    emptyCategories.forEach(categoryInfo => {
      const slotHTML = createEmptySlot(categoryInfo);
      listContainer.insertAdjacentHTML('beforeend', slotHTML);
    });

    // 更新产品数量显示
    const countEl = panel.querySelector('[data-unified-count]');
    const countTextEl = panel.querySelector('[data-unified-count-text]');
    if (countEl && countTextEl) {
      const totalQuantity = allProducts.reduce((sum, p) => sum + p.quantity, 0);
      if (totalQuantity > 0) {
        countTextEl.textContent = `${totalQuantity} Item(s)`;
        countEl.style.display = 'block';
      } else {
        countEl.style.display = 'none';
      }
    }

    // 更新Clear按钮显示
    const clearBtn = panel.querySelector('[data-unified-clear]');
    if (clearBtn) {
      clearBtn.style.display = allProducts.length > 0 ? 'inline-block' : 'none';
    }

    console.log('[Bundle Campaign] Unified panel rendered successfully');
    console.log('[Bundle Campaign] Total products:', allProducts.length);
  }

  /**
   * 设置统一面板事件监听
   */
  function setupUnifiedPanelEvents(builder) {
    const panel = builder.querySelector('[data-unified-panel]');
    if (!panel) return;

    console.log('[Bundle Campaign] Setting up unified panel events');

    // Clear按钮
    const clearBtn = panel.querySelector('[data-unified-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        console.log('[Bundle Campaign] Clear button clicked');
        if (confirm('Are you sure you want to clear all products from your bundle?')) {
          // 清空所有类目
          ['collection_1', 'collection_2', 'collection_3', 'collection_4'].forEach(categoryId => {
            if (builder.state.collections[categoryId]) {
              builder.state.collections[categoryId].selectedProducts.clear();
              // 更新类目卡片
              if (builder.updateCategoryCard) {
                builder.updateCategoryCard(categoryId);
                fixCategoryCardDisplay(builder, categoryId);
              }
            }
          });

          // 更新UI
          if (builder.updateCartBadges) builder.updateCartBadges();
          if (builder.updateAddToCartButton) builder.updateAddToCartButton();
          renderUnifiedBundlePanel(builder);
        }
      });
    }

    // 委托事件：删除产品按钮
    panel.addEventListener('click', (e) => {
      if (e.target.closest('[data-unified-remove]')) {
        const item = e.target.closest('[data-unified-item]');
        const variantId = parseInt(item.dataset.variantId);
        const categoryId = item.dataset.categoryId;

        console.log('[Bundle Campaign] Remove product from unified panel:', variantId, categoryId);

        if (builder.state.collections[categoryId]) {
          builder.state.collections[categoryId].selectedProducts.delete(variantId);

          // 同步更新所有产品卡片状态
          updateAllProductCardsState(builder, variantId, categoryId);

          // 更新类目卡片
          if (builder.updateCategoryCard) {
            builder.updateCategoryCard(categoryId);
            fixCategoryCardDisplay(builder, categoryId);
          }

          // 更新UI
          if (builder.updateCartBadges) builder.updateCartBadges();
          if (builder.updateAddToCartButton) builder.updateAddToCartButton();
          renderUnifiedBundlePanel(builder);
        }
      }

      // 空状态槽位的Add按钮或整个空槽位div
      const emptySlotClick = e.target.closest('[data-unified-add-empty]') || e.target.closest('[data-unified-empty]');
      if (emptySlotClick) {
        const emptySlot = e.target.closest('[data-unified-empty]');
        if (!emptySlot) return;

        const categoryId = emptySlot.dataset.categoryId;

        console.log('[Bundle Campaign] Empty slot clicked:', categoryId);
        console.log('[Bundle Campaign] Window width:', window.innerWidth);

        // Desktop模式：切换产品网格
        if (window.innerWidth >= 990) {
          console.log('[Bundle Campaign] Desktop mode - switching grid');
          if (!builder._productGridsContainer) {
            console.error('[Bundle Campaign] Product grids container not found!');
            return;
          }
          switchProductGrid(builder, categoryId);
        } else {
          // Mobile模式：打开对应类目的Modal
          console.log('[Bundle Campaign] Mobile mode - opening modal');
          const modal = document.querySelector(`#BundleModal-${categoryId}`);
          console.log('[Bundle Campaign] Modal element:', modal);

          if (modal) {
            if (typeof modal.show === 'function') {
              console.log('[Bundle Campaign] Opening modal with show()');
              modal.show();
            } else {
              console.error('[Bundle Campaign] Modal.show() is not a function');
            }
          } else {
            console.error('[Bundle Campaign] Modal not found:', `#BundleModal-${categoryId}`);
          }
        }
      }
    });
  }

  /**
   * 初始化View More/Less功能
   */
  function setupViewMoreLess(builder) {
    console.log('========================================');
    console.log('[View More] FUNCTION START - setupViewMoreLess');
    console.log('[View More] Builder:', builder);
    console.log('========================================');

    const productGridsContainer = builder._productGridsContainer;
    console.log('[View More] productGridsContainer:', productGridsContainer);

    if (!productGridsContainer) {
      console.log('[View More] ERROR: No product grids container found!');
      return;
    }

    // 为每个产品网格设置View More/Less
    const productGrids = productGridsContainer.querySelectorAll('[data-product-grid]');
    console.log('[View More] Found product grids:', productGrids.length);
    console.log('[View More] Product grids:', productGrids);

    productGrids.forEach((grid, index) => {
      const categoryId = grid.dataset.collectionId;
      const viewMoreBtn = grid.querySelector('[data-view-more-btn]');
      const viewMoreText = viewMoreBtn?.querySelector('.bundle-view-more-text');
      const productsGridInner = grid.querySelector('[data-products-grid-inner]');

      console.log(`[View More] Grid ${index + 1}:`, {
        categoryId,
        hasViewMoreBtn: !!viewMoreBtn,
        hasProductsGridInner: !!productsGridInner,
        grid: grid
      });

      if (!viewMoreBtn || !productsGridInner) {
        console.log(`[View More] Skipping grid ${categoryId} - missing button or products container`);
        return;
      }

      // 获取初始显示数量
      const initialCountPc = parseInt(grid.dataset.initialCountPc) || 6;
      const initialCountMobile = parseInt(grid.dataset.initialCountMobile) || 4;

      // 获取当前类目的所有产品卡片
      function getCurrentGridProducts() {
        return Array.from(productsGridInner.querySelectorAll('.bundle-product-card'));
      }

      // 根据屏幕宽度获取初始显示数量
      function getInitialVisibleCount() {
        const width = window.innerWidth;
        if (width >= 990) return initialCountPc;  // PC: 6
        return initialCountMobile;  // Mobile: 4
      }

      let initialVisibleCount = getInitialVisibleCount();
      let isExpanded = false;

      // 更新按钮显示状态
      function updateButtonVisibility() {
        const products = getCurrentGridProducts();
        const viewMoreContainer = grid.querySelector('[data-view-more-container]');
        if (!viewMoreContainer) {
          console.log(`[View More] No container found for ${categoryId}`);
          return;
        }

        const shouldShow = products.length > initialVisibleCount;
        console.log(`[View More] ${categoryId}: ${products.length} products, initial visible: ${initialVisibleCount}, shouldShow: ${shouldShow}`);

        if (shouldShow) {
          viewMoreContainer.style.display = 'flex';
        } else {
          viewMoreContainer.style.display = 'none';
        }
      }

      // 初始化时更新按钮显示状态
      updateButtonVisibility();

      // View More/Less按钮点击处理
      viewMoreBtn.addEventListener('click', function() {
        const products = getCurrentGridProducts();
        isExpanded = !isExpanded;

        if (isExpanded) {
          // 展开：显示所有产品
          products.forEach(product => {
            product.classList.remove('bundle-product-card--hidden');
            product.style.display = '';
          });
          viewMoreText.textContent = 'View Less';
          viewMoreBtn.dataset.state = 'expanded';

          // 更新图标：从+ 改为 -
          const icon = viewMoreBtn.querySelector('.bundle-view-more-icon');
          if (icon) {
            icon.innerHTML = '<path d="M2 6H10M10 6L6 2M10 6L6 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>';
          }
        } else {
          // 收起：只显示初始数量
          products.forEach((product, index) => {
            if (index >= initialVisibleCount) {
              product.classList.add('bundle-product-card--hidden');
            }
          });
          viewMoreText.textContent = 'View More';
          viewMoreBtn.dataset.state = 'collapsed';

          // 更新图标：从- 改为 +
          const icon = viewMoreBtn.querySelector('.bundle-view-more-icon');
          if (icon) {
            icon.innerHTML = '<path d="M2 6H10M10 6L6 2M10 6L6 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>';
          }

          // 滚动到产品网格顶部
          setTimeout(() => {
            const gridTop = grid.getBoundingClientRect().top + window.pageYOffset;
            const scrollTarget = gridTop - 100; // 100px offset
            window.scrollTo({
              top: scrollTarget,
              behavior: 'smooth'
            });
          }, 100);
        }

        console.log('[Bundle Campaign] View More/Less toggled:', { categoryId, isExpanded });
      });

      // 监听窗口resize
      let resizeTimeout;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const newCount = getInitialVisibleCount();
          if (newCount !== initialVisibleCount) {
            initialVisibleCount = newCount;
            const products = getCurrentGridProducts();

            // 如果是收起状态，重新应用显示逻辑
            if (!isExpanded) {
              products.forEach((product, index) => {
                if (index < initialVisibleCount) {
                  product.classList.remove('bundle-product-card--hidden');
                  product.style.display = '';
                } else {
                  product.classList.add('bundle-product-card--hidden');
                }
              });
            }

            // 更新按钮显示状态
            updateButtonVisibility();
          }
        }, 250);
      });
    });

    console.log('[Bundle Campaign] View More/Less setup complete');
  }

  /**
   * 查找并初始化所有活动页模式的builder
   */
  function findAndInitBuilders() {
    // 🔧 首先修复DOM位置
    fixBundleCampaignPosition();

    const builders = document.querySelectorAll('product-bundle-builder[data-campaign-mode="true"]');
    console.log('[Bundle Campaign] Found builders:', builders.length);

    builders.forEach(builder => {
      // 等待元素完全初始化
      if (builder.isConnected) {
        initCampaignMode(builder);
      } else {
        // 如果还未连接到DOM,等待一下
        setTimeout(() => initCampaignMode(builder), 200);
      }
    });

    // 如果没有找到,可能还在加载中,再试一次
    if (builders.length === 0) {
      console.log('[Bundle Campaign] No builders found yet, will retry...');
      setTimeout(findAndInitBuilders, 300);
    }
  }

  // 启动初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(findAndInitBuilders, 100);
    });
  } else {
    setTimeout(findAndInitBuilders, 100);
  }

  console.log('[Bundle Campaign Extension] Loaded');
})();
